// Joana client — runs on the MAIN thread. The app-facing handle to a mini-app
// running in the Joana Worker. It posts launch/call commands, awaits results,
// surfaces audit events, and — crucially — services the UI-bearing capability
// requests the worker cannot perform itself (payments confirm sheet, share
// sheet, local notifications, camera, …). Those are the trusted, DOM/OS-touching
// effects; the worker proxies them here, the app's registered handler runs them
// (showing trusted UI), and the answer goes back to the sandbox.

import type { EvalResult } from "../engine/isolate.ts";
import type { MiniAppManifest } from "../runtime/manifest.ts";
import { applyFrame, type Renderer } from "../render/renderer.ts";
import type { RenderEvent } from "../render/patch.ts";
import type {
  AuditMsg,
  CapabilityRequestMsg,
  HostToClient,
  RenderFrameMsg,
  ResultMsg,
  Transport,
} from "./protocol.ts";

/** Builds the main-thread renderer, wired so its captured events post back to the
 *  worker. The browser passes a DOM-renderer factory; tests pass a recording one. */
export type RendererFactory = (onEvent: (e: RenderEvent) => void) => Renderer;

/** A main-thread capability implementation. `op` is the full dotted capability
 *  (e.g. `payments.intent`); returns the value to resolve the mini-app with, or
 *  throws to reject it. */
export type MainCapabilityHandler = (
  op: string,
  args: Record<string, unknown>,
) => Promise<unknown> | unknown;

export interface AuditEvent {
  appId: string;
  capability: string;
  decision: string;
  detail?: string;
}

export class JoanaClient {
  private nextReqId = 1;
  private readonly pending = new Map<
    number,
    { resolve: (r: EvalResult) => void; reject: (e: unknown) => void }
  >();
  private readonly capabilityHandlers = new Map<string, MainCapabilityHandler>();

  /** Set to observe every broker decision (gating visible live in the UI). */
  onAudit: ((e: AuditEvent) => void) | null = null;

  private readonly renderer: Renderer | null;

  private readonly transport: Transport;

  constructor(
    transport: Transport,
    opts: { renderer?: RendererFactory } = {},
  ) {
    this.transport = transport;
    // The renderer's captured events post straight back to the worker, which
    // decodes the bound action and re-renders.
    this.renderer = opts.renderer
      ? opts.renderer((event) => this.transport.post({ t: "renderEvent", event }))
      : null;
    transport.onMessage((msg) => {
      void this.onMessage(msg as HostToClient);
    });
  }

  /** Register the main-thread implementation for a capability grant (e.g.
   *  `payments.intent`). The worker proxies matching host calls here. */
  registerCapability(grant: string, handler: MainCapabilityHandler): void {
    this.capabilityHandlers.set(grant, handler);
  }

  /** Launch a mini-app: evaluate its bundle + service its capability loop. */
  launch(manifest: MiniAppManifest, bundle: string): Promise<EvalResult> {
    return this.request((reqId) => ({ t: "launch", reqId, manifest, bundle }));
  }

  /** Forward a UI/event handler invocation into the running mini-app. */
  callHandler(fn: string, args: unknown[] = []): Promise<EvalResult> {
    return this.request((reqId) => ({ t: "call", reqId, fn, args }));
  }

  /** Deterministic teardown — drop the isolate in the worker. */
  dispose(): void {
    this.transport.post({ t: "dispose" });
    for (const { reject } of this.pending.values()) {
      reject(new Error("Joana client disposed"));
    }
    this.pending.clear();
  }

  private request(make: (reqId: number) => unknown): Promise<EvalResult> {
    const reqId = this.nextReqId++;
    return new Promise<EvalResult>((resolve, reject) => {
      this.pending.set(reqId, { resolve, reject });
      this.transport.post(make(reqId));
    });
  }

  private async onMessage(msg: HostToClient): Promise<void> {
    switch (msg.t) {
      case "result":
        return this.settle(msg);
      case "error": {
        if (msg.reqId !== undefined) {
          const p = this.pending.get(msg.reqId);
          if (p) {
            this.pending.delete(msg.reqId);
            p.reject(new Error(msg.message));
          }
        }
        return;
      }
      case "audit":
        this.onAudit?.(toAudit(msg));
        return;
      case "renderFrame":
        return this.applyRenderFrame(msg);
      case "capabilityRequest":
        return this.serviceCapability(msg);
    }
  }

  private applyRenderFrame(msg: RenderFrameMsg): void {
    if (this.renderer) applyFrame(this.renderer, msg.frame);
  }

  private settle(msg: ResultMsg): void {
    const p = this.pending.get(msg.reqId);
    if (!p) return;
    this.pending.delete(msg.reqId);
    p.resolve({ status: msg.status, value: msg.value, error: msg.error });
  }

  // Resolve the handler for this op (grant == op, or op under the grant family),
  // run it, and reply. A missing handler or a throw rejects the mini-app's call.
  private async serviceCapability(msg: CapabilityRequestMsg): Promise<void> {
    const handler = this.handlerFor(msg.capability);
    if (!handler) {
      this.transport.post({
        t: "capabilityReply",
        callId: msg.callId,
        ok: false,
        value: `no main-thread handler for ${msg.capability}`,
      });
      return;
    }
    try {
      const value = await handler(msg.capability, asObject(msg.args));
      this.transport.post({ t: "capabilityReply", callId: msg.callId, ok: true, value });
    } catch (e) {
      this.transport.post({
        t: "capabilityReply",
        callId: msg.callId,
        ok: false,
        value: e instanceof Error ? e.message : String(e),
      });
    }
  }

  private handlerFor(op: string): MainCapabilityHandler | undefined {
    for (const [grant, handler] of this.capabilityHandlers) {
      if (op === grant || op.startsWith(`${grant}.`)) return handler;
    }
    return undefined;
  }
}

function toAudit(m: AuditMsg): AuditEvent {
  return { appId: m.appId, capability: m.capability, decision: m.decision, detail: m.detail };
}

function asObject(args: unknown): Record<string, unknown> {
  return args && typeof args === "object" ? (args as Record<string, unknown>) : {};
}
